import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Star, Heart, Gift, Mail, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';


export const FeedbackPage = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ebookUrl, setEbookUrl] = useState('');
  const [existingFeedback, setExistingFeedback] = useState(null);
  
  const [feedback, setFeedback] = useState({
    overall_rating: 0,
    experience_rating: 0,
    recommendation_rating: 0,
    what_loved: '',
    improvements: '',
    would_recommend: true,
    join_mailing_list: false,
    testimonial_permission: false,
    additional_comments: ''
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    } else if (user && consultationId) {
      checkExistingFeedback();
    }
  }, [user, authLoading, consultationId]);

  const checkExistingFeedback = async () => {
    try {
      const response = await api.get(`/api/feedback/${consultationId}`);
      if (response.data) {
        setExistingFeedback(response.data);
        setSubmitted(true);
        setEbookUrl('https://aiays.org/wp-content/uploads/2024/09/Your-First-Step-to-Ayurveda.pdf');
      }
    } catch (error) {
      // No feedback yet - that's fine
    }
  };

  const handleRating = (field, value) => {
    setFeedback(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (feedback.overall_rating === 0) {
      toast.error('Please provide an overall rating');
      return;
    }
    if (!feedback.what_loved.trim()) {
      toast.error('Please share what you loved about your experience');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post(`/api/feedback`, {
        consultation_id: consultationId,
        ...feedback
      });
      
      setSubmitted(true);
      setEbookUrl(response.data.ebook_url);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ field, label, value }) => (
    <div className="mb-6">
      <Label className="text-gray-700 mb-2 block">{label}</Label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRating(field, star)}
            className="transition-transform hover:scale-110"
          >
            <Star 
              className={`w-8 h-8 ${
                star <= value 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-300'
              }`} 
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Success/Thank You Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white flex items-center justify-center px-4" data-testid="feedback-success">
        <div className="max-w-xl w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank You! 🙏
            </h1>
            
            <p className="text-gray-600 mb-8">
              Your feedback is invaluable to us. It helps us serve you and others better on their wellness journey.
            </p>
            
            <div className="bg-gradient-to-br from-[hsl(22,70%,44%)] to-[hsl(35,80%,45%)] rounded-xl p-6 text-white mb-8">
              <Gift className="w-12 h-12 mx-auto mb-4 opacity-90" />
              <h2 className="text-xl font-semibold mb-2">Your Free Gift!</h2>
              <p className="text-white/90 mb-4 text-sm">
                As a token of our appreciation, here's your free Ayurveda eBook:
              </p>
              <p className="font-bold text-lg mb-4">"Your First Step to Ayurveda"</p>
              <a 
                href={ebookUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[hsl(22,70%,44%)] px-6 py-3 rounded-full font-semibold hover:bg-white/90 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download eBook
              </a>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              We've also sent the eBook to your email for easy access anytime.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Go to Dashboard
              </Button>
              <Button onClick={() => navigate('/panchkarma')} className="btn-primary">
                Explore Panchkarma
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Feedback Form
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-12 px-4" data-testid="feedback-form">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img 
            src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
            alt="Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            We'd Love Your Feedback!
          </h1>
          <p className="text-gray-600">
            Your experience matters to us. Share your thoughts and receive a special gift!
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* Overall Rating */}
            <div className="text-center mb-8 pb-6 border-b border-gray-100">
              <Label className="text-lg text-gray-700 mb-3 block">How was your overall experience?</Label>
              <div className="flex justify-center gap-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRating('overall_rating', star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star 
                      className={`w-10 h-10 ${
                        star <= feedback.overall_rating 
                          ? 'fill-yellow-400 text-yellow-400' 
                          : 'text-gray-300 hover:text-yellow-300'
                      }`} 
                    />
                  </button>
                ))}
              </div>
              {feedback.overall_rating > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {feedback.overall_rating === 5 ? 'Excellent!' : 
                   feedback.overall_rating === 4 ? 'Great!' : 
                   feedback.overall_rating === 3 ? 'Good' : 
                   feedback.overall_rating === 2 ? 'Fair' : 'Needs Improvement'}
                </p>
              )}
            </div>

            {/* Detailed Ratings */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <StarRating 
                field="experience_rating" 
                label="Quality of Recommendations" 
                value={feedback.experience_rating} 
              />
              <StarRating 
                field="recommendation_rating" 
                label="Ease of Use" 
                value={feedback.recommendation_rating} 
              />
            </div>

            {/* What They Loved */}
            <div className="mb-6">
              <Label htmlFor="what_loved" className="text-gray-700 mb-2 block flex items-center gap-2">
                <Heart className="w-4 h-4 text-red-400" />
                What did you love about your experience?
              </Label>
              <Textarea
                id="what_loved"
                value={feedback.what_loved}
                onChange={(e) => setFeedback(prev => ({ ...prev, what_loved: e.target.value }))}
                placeholder="Share what made your experience special..."
                className="min-h-[100px]"
                data-testid="feedback-loved"
              />
            </div>

            {/* Improvements */}
            <div className="mb-6">
              <Label htmlFor="improvements" className="text-gray-700 mb-2 block">
                How can we improve? (Optional but appreciated!)
              </Label>
              <Textarea
                id="improvements"
                value={feedback.improvements}
                onChange={(e) => setFeedback(prev => ({ ...prev, improvements: e.target.value }))}
                placeholder="Your suggestions help us serve you better..."
                className="min-h-[80px]"
                data-testid="feedback-improvements"
              />
            </div>

            {/* Would Recommend */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <Label className="text-gray-700 mb-3 block">Would you recommend us to friends and family?</Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFeedback(prev => ({ ...prev, would_recommend: true }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    feedback.would_recommend 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  Yes, definitely! 👍
                </button>
                <button
                  type="button"
                  onClick={() => setFeedback(prev => ({ ...prev, would_recommend: false }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                    !feedback.would_recommend 
                      ? 'border-orange-500 bg-orange-50 text-orange-700' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  Not yet
                </button>
              </div>
            </div>

            {/* Additional Comments */}
            <div className="mb-6">
              <Label htmlFor="additional" className="text-gray-700 mb-2 block">
                Anything else you'd like to share? (Optional)
              </Label>
              <Textarea
                id="additional"
                value={feedback.additional_comments}
                onChange={(e) => setFeedback(prev => ({ ...prev, additional_comments: e.target.value }))}
                placeholder="Any other thoughts, suggestions, or messages..."
                className="min-h-[60px]"
              />
            </div>

            {/* Permission Checkboxes */}
            <div className="space-y-4 mb-8 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-100">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="mailing"
                  checked={feedback.join_mailing_list}
                  onCheckedChange={(checked) => setFeedback(prev => ({ ...prev, join_mailing_list: checked }))}
                  className="mt-1"
                />
                <Label htmlFor="mailing" className="text-sm text-gray-700 cursor-pointer">
                  <span className="font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[hsl(22,70%,44%)]" />
                    Join our wellness community
                  </span>
                  <span className="text-gray-500 block mt-1">
                    Receive Ayurvedic tips, exclusive offers, and wellness insights. (We respect your inbox - only valuable content!)
                  </span>
                </Label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="testimonial"
                  checked={feedback.testimonial_permission}
                  onCheckedChange={(checked) => setFeedback(prev => ({ ...prev, testimonial_permission: checked }))}
                  className="mt-1"
                />
                <Label htmlFor="testimonial" className="text-sm text-gray-700 cursor-pointer">
                  <span className="font-medium">Share my story</span>
                  <span className="text-gray-500 block mt-1">
                    I'm happy for my feedback to be used as a testimonial to help others discover Ayurveda. (First name only)
                  </span>
                </Label>
              </div>
            </div>

            {/* Gift Notice */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-lg p-4 mb-6 flex items-center gap-4">
              <Gift className="w-10 h-10 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Free Gift Awaits!</p>
                <p className="text-sm text-green-700">
                  Submit your feedback and receive our exclusive Ayurveda eBook - "Your First Step to Ayurveda"
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary py-6 text-lg"
              disabled={loading}
              data-testid="submit-feedback"
            >
              {loading ? 'Submitting...' : 'Submit Feedback & Get My Free eBook'}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Your feedback is confidential and helps us improve our services.
        </p>
      </div>
    </div>
  );
};
